import React, { useState } from 'react';
import { formatTemperature } from '../../utils/temperatureUtils';
import { getRelativeTime, formatDate, formatDuration } from '../../utils/dateUtils';
import { useAlerts } from '../../context/AlertContext';
import AcknowledgeReasonModal from './AcknowledgeReasonModal';
import '../../styles/alerts.css';

const AlertCard = ({ alert, onClick, onAcknowledged }) => {
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const { acknowledgeAlert } = useAlerts();

  const handleAcknowledgeClick = (e) => {
    e.stopPropagation();
    setShowReasonModal(true);
  };

  const handleReasonConfirm = async (reason) => {
    setIsAcknowledging(true);
    const result = await acknowledgeAlert(alert.id, reason);
    setIsAcknowledging(false);
    setShowReasonModal(false);
    if (result?.data && !result?.error && onAcknowledged) {
      onAcknowledged(alert.id);
    }
  };

  const handleReasonCancel = () => {
    setShowReasonModal(false);
  };
  
  const severityClass = alert.severity === 'critical' ? 'danger' : 'warning';
  
  const getIcon = () => {
    switch (alert.type) {
      case 'HIGH_TEMP':
      case 'CRITICAL_HIGH':
        return (
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
          </svg>
        );
      case 'LOW_TEMP':
      case 'CRITICAL_LOW':
        return (
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1zm-5 8.274l-.818 2.552c.25.112.526.174.818.174.292 0 .569-.062.818-.174L5 10.274zm10 0l-.818 2.552c.25.112.526.174.818.174.292 0 .569-.062.818-.174L15 10.274z" clipRule="evenodd" />
          </svg>
        );
      case 'SENSOR_OFFLINE':
        return (
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
          </svg>
        );
      case 'RAPID_CHANGE':
        return (
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };
  
  return (
    <>
    <div
      onClick={onClick}
      className={`alert-card alert-card--${severityClass} ${alert.acknowledged ? 'alert-card--acknowledged' : ''}`}
      role="article"
      aria-label={`Alert: ${alert.severity} for ${alert.vehiclePlate}`}
    >
      <div className="alert-card__content">
        <div className={`alert-card__icon alert-card__icon--${severityClass}`}>
          {getIcon()}
        </div>

        <div className="alert-card__body">
          <div className="alert-card__header">
            <div>
              <h3 className="alert-card__title">
                {alert.vehiclePlate} - {alert.doorName}
              </h3>
              <span className={`alert-card__badge alert-card__badge--${severityClass}`}>
                {alert.severity?.toUpperCase() || 'CRITICAL'}
              </span>
              {alert.categoryName && alert.categoryName !== 'default' && (
                <span className="alert-card__badge alert-card__badge--category">
                  {alert.categoryName}
                </span>
              )}
            </div>

            {!alert.acknowledged && (
              <button
                onClick={handleAcknowledgeClick}
                disabled={isAcknowledging}
                className="btn-secondary"
                style={{ fontSize: 'var(--font-size-xs)', padding: '0.25rem 0.75rem' }}
              >
                {isAcknowledging ? 'Processing...' : 'Acknowledge'}
              </button>
            )}
          </div>

          <div className="alert-card__details">
            {alert.temperature !== null && (
              <p>
                <span className="alert-card__detail-label">Temperature:</span>
                <span className={`alert-card__temp alert-card__temp--${severityClass}`}>
                  {formatTemperature(alert.temperature)}
                </span>
                {alert.normalRange && (
                  <span className="alert-card__normal-range">
                    (Normal: {formatTemperature(alert.normalRange.min)} to {formatTemperature(alert.normalRange.max)})
                  </span>
                )}
              </p>
            )}

            <p>
              <span className="alert-card__detail-label">Occurrence Time:</span> {alert.occurenceTime || (alert.timestamp ? alert.timestamp.split(' ')[1] : '--')}
            </p>

            <p>
              <span className="alert-card__detail-label">Date:</span> {alert.timestamp ? formatDate(alert.timestamp) : '--'}
              {alert.timestamp && (
                <span className="alert-card__time-relative">
                  ({getRelativeTime(alert.timestamp)})
                </span>
              )}
            </p>

            {alert.duration > 0 && (
              <p>
                <span className="alert-card__detail-label">Duration:</span> {formatDuration(alert.duration)}
              </p>
            )}

            {alert.acknowledged && (
              <p className="alert-card__status-text alert-card__status-text--acknowledged">
                Acknowledged{alert.acknowledgedBy ? ` by ${alert.acknowledgedBy}` : ''}{alert.acknowledgedAt ? ` at ${formatDate(alert.acknowledgedAt)}` : ''}
              </p>
            )}

            {alert.notes && (
              <p className="alert-card__note">
                Note: {alert.notes}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>

    {showReasonModal && (
      <AcknowledgeReasonModal
        alert={alert}
        onConfirm={handleReasonConfirm}
        onCancel={handleReasonCancel}
        isSubmitting={isAcknowledging}
      />
    )}
    </>
  );
};

export default AlertCard;

